/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/shared/PageTabs.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/shared/PageTabs.tsx
 * filename: PageTabs.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { Link, matchPath, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

export interface PageTab {
  label: string;
  href: string;
  /** If true, only match exact path (default: true) */
  end?: boolean;
  /** Explicit selection for query-owned tabs that share one pathname. */
  active?: boolean;
  /** Fallback on this pathname when its query does not select another tab. */
  default?: boolean;
}

export function resolveActivePageTab(tabs: readonly PageTab[], pathname: string, search = '') {
  const explicit = tabs.find((tab) => tab.active === true);
  if (explicit) return explicit;
  const current = new URLSearchParams(search);
  let best: PageTab | undefined;
  let bestScore = -1;
  let fallback: PageTab | undefined;
  for (const tab of tabs) {
    if (tab.active === false) continue;
    const target = new URL(tab.href, 'https://navigation.local');
    if (!matchPath({ path: target.pathname, end: tab.end ?? true }, pathname)) continue;
    if (tab.default) fallback ??= tab;
    if (![...target.searchParams].every(([key, value]) => current.get(key) === value)) continue;
    const score = target.pathname.length + target.searchParams.size * 10000;
    if (score > bestScore) {
      best = tab;
      bestScore = score;
    }
  }
  return best ?? fallback;
}

interface PageTabsProps {
  tabs: PageTab[];
  className?: string;
  accentColor?: string;
}

/**
 * PageTabs — horizontal tab bar that uses NavLink for route-based sub-navigation.
 * Uses a sliding motion indicator that follows the active tab.
 */
export function PageTabs({ tabs, className, accentColor }: PageTabsProps) {
  const location = useLocation();
  const uniqueTabs = useMemo(
    () =>
      tabs.filter(
        (tab, index) => tabs.findIndex((candidate) => candidate.href === tab.href) === index
      ),
    [tabs]
  );
  const tabRefs = useRef<Map<string, HTMLAnchorElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const activeTab = resolveActivePageTab(uniqueTabs, location.pathname, location.search);

  const updateIndicator = useCallback(() => {
    if (activeTab) {
      const el = tabRefs.current.get(activeTab.href);
      const container = containerRef.current;
      if (el && container) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = el.getBoundingClientRect();
        setIndicatorStyle({
          left: tabRect.left - containerRect.left,
          width: tabRect.width,
        });
      }
    } else {
      setIndicatorStyle({ left: 0, width: 0 });
    }
  }, [activeTab]);

  useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  return (
    <nav
      ref={containerRef}
      aria-label="Section tabs"
      role="tablist"
      className={clsx(
        'relative flex max-w-full flex-wrap items-center gap-1 border-b border-[rgb(var(--color-border))] sm:flex-nowrap',
        className
      )}
    >
      {uniqueTabs.map((tab, index) => {
        const selected = tab === activeTab;
        return (
          <Link
            key={tab.href}
            to={tab.href}
            role="tab"
            aria-selected={selected}
            aria-current={selected ? 'page' : undefined}
            tabIndex={selected || (!activeTab && index === 0) ? 0 : -1}
            onKeyDown={(event) => {
              let nextIndex: number | undefined;
              if (event.key === 'ArrowRight') nextIndex = (index + 1) % uniqueTabs.length;
              if (event.key === 'ArrowLeft')
                nextIndex = (index - 1 + uniqueTabs.length) % uniqueTabs.length;
              if (event.key === 'Home') nextIndex = 0;
              if (event.key === 'End') nextIndex = uniqueTabs.length - 1;
              if (nextIndex === undefined) return;
              event.preventDefault();
              tabRefs.current.get(uniqueTabs[nextIndex].href)?.focus();
            }}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.href, el);
            }}
            className={clsx(
              'rounded-t-md px-2 py-2 text-sm font-medium transition-colors sm:px-3',
              selected
                ? 'bg-[rgb(var(--color-bg-secondary))] text-[rgb(var(--color-text-primary))] sm:bg-transparent'
                : 'text-[rgb(var(--color-text-tertiary))] hover:text-[rgb(var(--color-text-secondary))]'
            )}
          >
            {tab.label}
          </Link>
        );
      })}
      <motion.div
        className="absolute bottom-0 hidden h-0.5 rounded-full sm:block"
        style={{ backgroundColor: accentColor || 'currentColor' }}
        animate={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
      />
    </nav>
  );
}
